<template>
  <RevCard
    class="h-full overflow-hidden"
    :class="{ 'outline-default-hi': hasFocus }"
    data-qa="productCard"
    :hoverable="true"
  >
    <div
      ref="container"
      class="relative flex h-full flex-col pt-16"
      data-spec="product-card-content"
    >
      <div
        v-if="tags.length > 0"
        class="flex gap-6 px-16 pb-4"
        :class="{
          'absolute right-0': specifications,
        }"
        data-spec="tags"
      >
        <RevTag
          v-for="tag in tags"
          :key="tag"
          :label="tag"
          variant="secondary"
        />
      </div>
      <div class="flex grow flex-col p-16 pt-0" data-spec="main">
        <div
          class="flex grow flex-wrap gap-8"
          :class="{
            'justify-center': !specifications,
          }"
          data-spec="main-content"
        >
          <div class="flex flex-col gap-2" data-spec="image+colors">
            <RevLink :to="url">
              <RevIllustration
                :alt="imageAlt"
                :height="specifications ? 80 : 128"
                :src="imageSrc"
                :width="specifications ? 80 : 128"
              />
            </RevLink>
            <ColorSwatches v-if="!specifications" :colors="colorSwatches" />
          </div>

          <div
            class="flex grow flex-col justify-end gap-6"
            :class="{
              'basis-160': !specifications,
            }"
            data-spec="product-info"
          >
            <div class="flex flex-col gap-2" data-spec="titles">
              <RevLink :to="url" :underlined="false">
                <h2 class="body-1-bold line-clamp-2">
                  {{ title }}
                </h2>

                <h3 v-if="description" class="body-2 line-clamp-2">
                  {{ description }}
                </h3>

                <span v-if="warranty" class="body-2">
                  {{ i18n(translations.productCardWarranty, { warranty }) }}
                </span>

                <span v-if="grade" class="body-2">
                  {{ i18n(translations.gradeDescription, { grade }) }}
                </span>
              </RevLink>
            </div>

            <RevLink data-spec="rating" :to="url" :underlined="false">
              <ProductCardRatings
                :count="reviewRating?.count"
                :score="reviewRating?.average"
              />
            </RevLink>

            <RevLink
              data-spec="price-information"
              :to="url"
              :underlined="false"
            >
              <span
                v-if="withStartingFrom"
                class="text-static-default-low caption"
              >
                {{ i18n(translations.startingFrom) }}
              </span>
              <PriceWithCrossedPrice
                :price="i18n.price(price)"
                :price-new="priceNew && i18n.price(priceNew)"
              />
            </RevLink>
            <div>
              <ProductSpecifications
                v-if="specifications"
                :specifications="specifications"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-if="addToCartListener || withAddToCart" class="p-16 pt-0">
        <AddToCartButton
          :id="listingId"
          :text="i18n(translations.addToCart)"
          @add-to-cart="addToCart"
        />
      </div>
    </div>
  </RevCard>
</template>

<script lang="ts" setup>
import { getCurrentInstance, ref } from 'vue'

import { type Price } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'

import ColorSwatches from '~/scopes/product-list/components/ColorSwatches.vue'
import ProductSpecifications, {
  type Spec,
} from '~/scopes/product-list/components/ProductSpecifications.vue'

import AddToCartButton from './AddToCartButton.vue'
import PriceWithCrossedPrice from './PriceWithCrossedPrice.vue'
import translations from './ProductCard.translations'
import ProductCardRatings from './ProductCardRatings.vue'

export interface ProductCardProps {
  title: string
  description?: string
  imageSrc: string
  imageAlt?: string
  grade?: string
  url: string
  tags?: Array<string>
  price: Price
  priceNew?: Price
  reviewRating?: {
    count: number
    average: number
  }
  listingId: string
  warranty?: string
  colorSwatches?: Array<{ name: string; value: string }>
  withStartingFrom?: boolean
  addingToCart?: boolean
  withAddToCart?: boolean
  specifications?: Spec[]
}

const hasFocus = ref(false)

withDefaults(defineProps<ProductCardProps>(), {
  imageAlt: 'alt text is missing',
  description: undefined,
  grade: '',
  tags: () => [],
  priceNew: undefined,
  reviewRating: undefined,
  withStartingFrom: true,
  warranty: undefined,
  colorSwatches: () => [],
  addingToCart: false,
  withAddToCart: false,
  specifications: undefined,
})

const emit = defineEmits(['add-to-cart'])

const container = ref<HTMLDivElement | null>(null)
const i18n = useI18n()

defineExpose({ container })

const addToCartListener = getCurrentInstance()?.vnode.props?.onAddToCart

function addToCart({ status }: { status: string }) {
  emit('add-to-cart', { status })
}
</script>
